import {
  NumberField,
  type NumberFieldRootProps
} from '@base-ui/react/number-field';
import { type ComponentProps, useId } from 'react';

import { cn } from '@/lib/utils';

interface NewNumberFieldProps
  extends Omit<
    NumberFieldRootProps,
    'className' | 'defaultValue' | 'max' | 'min' | 'onBlur' | 'step' | 'value'
  > {
  className?: string;
  defaultValue?: NumberLike;
  max?: number | string;
  min?: number | string;
  onBlur?: ComponentProps<'input'>['onBlur'];
  onChange?: ComponentProps<'input'>['onChange'];
  placeholder?: string;
  rootClassName?: string;
  step?: number | string;
  value?: NumberLike;
}

type NumberLike = null | number | string | undefined;

const parseNumberLike = (value: NumberLike): null | number | undefined => {
  if (value === undefined) {
    return undefined;
  }

  if (value === null || value === '') {
    return null;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : null;
  }

  const normalized = value
    .replace(/,/g, '')
    .replace(/^\$/, '')
    .replace(/%$/, '')
    .trim();

  if (normalized === '') {
    return null;
  }

  const parsed = Number(normalized);
  return Number.isFinite(parsed) ? parsed : null;
};

const parseDefaultNumber = (value: NumberLike): number | undefined => {
  const parsed = parseNumberLike(value);
  return parsed ?? undefined;
};

const parseBound = (value: number | string | undefined): number | undefined => {
  if (value === undefined || value === '') {
    return undefined;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  const parsed = Number(value.trim());
  return Number.isFinite(parsed) ? parsed : undefined;
};

const parseStep = (
  value: number | string | undefined
): 'any' | number | undefined => {
  if (value === undefined || value === '') {
    return undefined;
  }

  if (value === 'any') {
    return value;
  }

  if (typeof value === 'number') {
    return Number.isFinite(value) ? value : undefined;
  }

  const parsed = Number(value.trim());
  return Number.isFinite(parsed) ? parsed : undefined;
};

const NewNumberField = ({
  className,
  defaultValue,
  id,
  max,
  min,
  onBlur,
  onChange,
  placeholder,
  rootClassName,
  step,
  value,
  ...numberFieldRootProps
}: NewNumberFieldProps) => {
  const generatedId = useId();
  const resolvedDefaultValue = parseDefaultNumber(defaultValue);
  const resolvedMax = parseBound(max);
  const resolvedMin = parseBound(min);
  const resolvedStep = parseStep(step);
  const resolvedValue = parseNumberLike(value);

  return (
    <NumberField.Root
      className={cn('min-w-0 flex-1', rootClassName)}
      defaultValue={resolvedDefaultValue}
      id={id ?? generatedId}
      max={resolvedMax}
      min={resolvedMin}
      step={resolvedStep}
      {...numberFieldRootProps}
      value={resolvedValue}
    >
      <NumberField.Input
        className={cn(
          'file:text-foreground placeholder:text-muted-foreground selection:bg-primary selection:text-primary-foreground dark:bg-input/30 border-input h-9 w-full min-w-0 rounded-md border bg-transparent px-3 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none file:inline-flex file:h-7 file:border-0 file:bg-transparent file:text-sm file:font-medium disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 md:text-sm',
          'focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]',
          'aria-invalid:ring-destructive/20 dark:aria-invalid:ring-destructive/40 aria-invalid:border-destructive',
          className
        )}
        data-slot="input-group-control"
        onBlur={onBlur}
        onChange={onChange}
        placeholder={placeholder}
      />
    </NumberField.Root>
  );
};

export default NewNumberField;
